import { CalendarOutlined } from "@ant-design/icons";
import {
  Alert,
  Button,
  Col,
  DatePicker,
  Divider,
  Form,
  Modal,
  Row,
  Select,
  Spin,
  Switch,
  Typography,
} from "antd";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { useEffect, useRef, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { Company } from "../../interfaces/company";
import {
  InvoicePaymentDetail,
  SalesInvoice,
} from "../../interfaces/invoice.types";
import { getAperturaCaja } from "../../services/aperturaDeCajaService";
import { getAllCajas } from "../../services/cajaService";
import { getCierreDeCajaByNo } from "../../services/cierreDeCajaService";
import { getEmpresa } from "../../services/empresaService";
import formaDePagoService from "../../services/formaDePagoService";
import { getPaymentLinesByNo } from "../../services/invoicePaymentService";
import {
  getInvoicesByDateRange,
  getInvoicesLines,
} from "../../services/invoiceService";
import { getAllTurnos } from "../../services/turnoService";
import { fetchFromBusinessCentral } from "../../utils/businessCentralUtils";
import { downloadImage } from "../../utils/utils";
import CuadreFP from "./cuadre-fp/CuadreFP";
import CuadrePV from "./cuadre-pv/CuadrePV";
import InvoicesList from "./invoice-list/InvoiceList";

dayjs.extend(customParseFormat);

const { Title } = Typography;

export const ReportType = {
  CUADRE_PV: "1",
  CUADRE_FP: "2",
  CAJERO_FP_DETALLE: "3",
};

export const ReportTypeOptions = [
  { value: ReportType.CUADRE_PV, label: "Cuadre PV" },
  { value: ReportType.CUADRE_FP, label: "Cuadre FP" },
  {
    value: ReportType.CAJERO_FP_DETALLE,
    label: "Cajero forma de pago detalle",
  },
];

export interface SelectOption {
  label: string;
  value: string | number;
}

interface TotalsResult {
  cash: number;
  card: number;
  disc: number;
  ret: number;
}

const Reports = () => {
  const [form] = Form.useForm();
  const [open, setOpen] = useState(false);
  const { currentUser } = useAuth() as any;
  const [data, setData] = useState<any>(null);
  const [count, setCount] = useState(() => {
    const saved = localStorage.getItem("count");
    return saved ? JSON.parse(saved) : 0;
  });
  const [company, setCompany] = useState<Company | null>(null);
  const [reportType, setReportType] = useState(null);
  const [turn, setTurn] = useState<number>(0);
  const [turns, setTurns] = useState<SelectOption[]>([]);
  const [cashBox, setCashBox] = useState("");
  const [cashBoxes, setCachBoxes] = useState<SelectOption[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState("");
  const [checked, setChecked] = useState(false);

  const componentRef = useRef(null);
  const now = dayjs();
  const formattedDate = now.format("DD/MM/YYYY");
  const toBCDate = (date: any) => dayjs(date).format("YYYY-MM-DD");

  const updateClickCount = () => {
    const newCount = count + 1;
    setCount(newCount);
    localStorage.setItem("clickCount", JSON.stringify(newCount));
    return newCount;
  };

  const getOpening = async (date: any) => {
    if (checked) {
      const start = toBCDate(date);
      const end = toBCDate(dayjs(date).add(1, "day"));

      const filter = [
        `FechaDeApertura ge ${start}`,
        `FechaDeApertura lt ${end}`,
        `Cajero eq '${currentUser.No}'`,
        `Turno eq ${turn}`,
      ];

      if (cashBox) {
        filter.push(`Caja eq '${cashBox}'`);
      }

      const openings = await fetchFromBusinessCentral(
        company!.Country_Region_Code !== "HN" ? "Apertura" : "AperturaDeCaja",
        filter.join(" and "),
      );

      const opening = openings.at(-1);

      return opening;
    } else {
      const openings = await getAperturaCaja(
        toBCDate(date),
        currentUser.No,
        turn,
        cashBox,
      );
      return openings.at(-1);
    }
  };

  const getInvoices = async (
    store: string | null,
    tpv: string | null,
    turn: number,
    date: any,
    dateTo: any = null,
  ) => {
    if (date == dateTo) {
      dateTo = null;
    }

    if (!dateTo) {
      dateTo = date;
    }

    if (checked) {
      const start = dayjs(date, "DD/MM/YY").format("YYYY-MM-DD");
      const end = dayjs(dateTo, "DD/MM/YY").add(1, "day").format("YYYY-MM-DD");

      var filter = [
        `Turno eq ${turn}`,
        `Posting_Date ge ${start}`,
        `Posting_Date lt ${end}`,
      ];

      if (tpv) {
        filter.push(`TPV eq '${tpv}'`);
      }

      if (store) {
        filter.push(`Tienda eq '${store}'`);
      }

      const invoices = await fetchFromBusinessCentral(
        "PostedSalesInvoice",
        filter.join(" and "),
      );

      return invoices as SalesInvoice[];
    } else {
      const invoices = await getInvoicesByDateRange(
        form.getFieldValue("date")?.format("YYYY-MM-DD"),
        form.getFieldValue("dateTo")?.format("YYYY-MM-DD"),
        turn,
        tpv,
      );

      return invoices;
    }
  };

  const getInvoicePaymentDetails = async (invoices?: any[]) => {
    if (checked) {
      const filter = invoices!
        .map((x) => `Document_No_ eq '${x.Pre_Assigned_No}'`)
        .join(" or ");

      const paymentDetails = await fetchFromBusinessCentral(
        "DetallePagoFactura",
        filter,
      );

      return paymentDetails as InvoicePaymentDetail[];
    } else {
      const invoiceNos: string[] = invoices!.map(
        (x) => x.Pre_Assigned_No || x.No,
      );
      const pLies = await getPaymentLinesByNo(invoiceNos);
      return pLies;
    }
  };

  const getInvoiceLines = async (invoices?: SalesInvoice[]) => {
    if (checked) {
      var filter = invoices!
        .map((x: any) => `Document_No eq '${x.No}'`)
        .join(" or ");

      const lines = await fetchFromBusinessCentral(
        "LinesPostedSalesInvoice",
        filter,
      );

      return lines;
    } else {
      var invoiceNos: string[] = invoices!.map((x:any) => checked ? x.No : x.id);
      const lines = await getInvoicesLines(invoiceNos);
      return lines;
    }
  };

  const getCreditMemos = async (
    store: string | null,
    tpv: string | null,
    turn: number,
    date: any,
  ) => {
    if (checked) {
      const start = dayjs(date, "DD/MM/YY").format("YYYY-MM-DD");
      const end = dayjs(date, "DD/MM/YY").add(1, "day").format("YYYY-MM-DD");

      var filter = [
        `Turno eq ${turn}`,
        `Posting_Date ge ${start}`,
        `Posting_Date lt ${end}`,
      ];

      if (store) {
        filter.push(`Tienda eq '${store}'`);
      }

      if (tpv) {
        filter.push(`Caja eq '${tpv}'`);
      }

      const creditMemos = await fetchFromBusinessCentral(
        "SalesCreditMemo",
        filter.join(" and "),
      );

      return creditMemos;
    } else {
      const creditMemos = await getInvoicesByDateRange(
        form.getFieldValue("date")?.format("YYYY-MM-DD"),
        form.getFieldValue("dateTo")?.format("YYYY-MM-DD"),
        turn,
        tpv,
        "Credit Memo",
      );

      return creditMemos;
    }
  };

  const getCreditNoteLines = async (
    documentNo: string,
    creditNotes?: any[],
  ) => {
    if (checked) {
      var filter = "";
      if (documentNo) {
        filter = `Document_No eq '${documentNo}'`;
      } else {
        filter = creditNotes!
          .map((x: any) => `Document_No eq '${x.No}'`)
          .join(" or ");
      }

      const creditMemoLines = await fetchFromBusinessCentral(
        "LinesSalesCreditMemo",
        filter,
      );
      return creditMemoLines;
    } else {
      var invoiceNos: string[] = [];
      if (documentNo) {
        invoiceNos.push(documentNo);
      } else {
        invoiceNos = creditNotes!.map((x) => x.No || x.id);
      }

      const lines = await getInvoicesLines(invoiceNos, "Credit Memo");

      return lines;
    }
  };

  const getClosing = async (no: string) => {
    let closing;
    if (checked) {
      const closings = await fetchFromBusinessCentral(
        "Cierres",
        `idApertura eq '${no}'`,
      );
      closing = closings[0];
    } else {
      closing = await getCierreDeCajaByNo(no);
    }

    return closing;
  };

  const cuadrePVReportData = async () => {
    const { date } = form.getFieldsValue();
    const opening = await getOpening(date);

    if (!opening) {
      setError("No se encontraron datos");
      return;
    }

    const closing = await getClosing(opening.No);
    if (!closing) {
      setError(` La caja ${cashBox} se encuentra abierta`);
    }

    const closingNo = closing.No.split(";");
    const store = closingNo[0];
    const tpv = closingNo[1];
    const closureDate = closingNo[2];
    const invoices = await getInvoices(store, tpv, turn, closureDate);

    let invoiceLines:any[]= [];
    let paymentLines:any[] = [];
    if (invoices.length) {
      invoiceLines = await getInvoiceLines(invoices);
      paymentLines = await getInvoicePaymentDetails(invoices);
    }

    const creditMemos = await getCreditMemos(null, cashBox, turn, date);
    let creditMemoLines: [] = [];
    if (creditMemos.length) {
      creditMemoLines = await getCreditNoteLines("", creditMemos);
    }

    const { cash, card, disc, ret } = await calculateTotals(
      invoices,
      invoiceLines,
      paymentLines,
      creditMemoLines,
    );
    
    const newCount = updateClickCount();
    const subTotal = cash + card;
    const sales = subTotal + disc;
    const ticketCount = invoices.length + creditMemos.length;

    setData({
      ...closing,
      ...opening,
      count: newCount,
      ticketCount: ticketCount,
      cach: cash,
      credit: card,
      discountAmount: disc,
      subTotal: subTotal,
      return: ret,
      sales: sales,
      tpv: tpv
    });

    setOpen(true);
  };

  const groupInvoiceLines = (lines: any[], payLines: any[]) => {
    const grouped: any = {};

    lines.forEach((l) => {
      const key = l.Document_No || l.invoiceNo || l.No || l.invoiceId;
      if (!grouped[key]) {
        grouped[key] = {
          no: key,
          total: 0,
          discount: 0,
        };
      }

      const disc = payLines
        .filter((x) => (x.Document_No_ = key))
        .reduce((s: any, x: any) => s + x.Dto || x.Descuento, 0) || 0;

      grouped[key].total += +l.Line_Amount || l.Unit_Price - disc;
      grouped[key].discount +=
        disc > 0
          ? disc
          : +l.Line_Discount_Amount || l.Unit_Price - l.Line_Amount || 0;
    });

    return Object.values(grouped);
  };

  const cuadreFPReportData = async () => {
    const { date } = form.getFieldsValue();
    const invoices = await getInvoices(null, null, turn, date);

    if (!invoices.length) {
      setError("No se encontraron datos");
      return;
    }

    const invoiceLines = await getInvoiceLines(invoices);
    const paymentLines = await getInvoicePaymentDetails(invoices);
    const creditMemos = await getCreditMemos(null, cashBox, turn, date);
    let creditMemoLines: [] = [];
    if (creditMemos.length) {
      creditMemoLines = await getCreditNoteLines("", creditMemos);
    }

    const { cash, card, disc, ret } = await calculateTotals(
      invoices,
      invoiceLines,
      paymentLines,
      creditMemoLines,
    );

    const subTotal = cash + card;
    const opening = await getOpening(date);
    const openingAmount = opening?.ImporteDeApertura || 0;

    const paymentForms = await formaDePagoService.getAllFormasDePago();

    const grouped = paymentLines
      .map((line: any) => {
        const fp = paymentForms.find(
          (p) => p.Code === (line.Payment_Method || line.Forma_de_Pago),
        );
        return {
          tipoPago: fp?.Tipo_Pago ?? "efectivo",
          amount: line.Amount || line.Importe,
        };
      })
      .reduce(
        (acc: any, curr: any) => {
          acc[curr.tipoPago] = (acc[curr.tipoPago] || 0) + curr.amount;
          return acc;
        },
        {} as Record<string, number>,
      );

    const result = Object.entries(grouped).map(([name, charged]) => ({
      name,
      charged,
    }));

    setData({
      date: form.getFieldValue("date")?.format("DD/MM/YYYY"),
      company: company,
      invoices: invoices,
      turn: turn,
      discount: disc,
      ret: ret,
      opening: openingAmount,
      subTotal: subTotal,
      cash: cash,
      paymentForms: result,
    });

    setOpen(true);
  };

  const invoicesListReportData = async () => {
    const { date, dateTo } = form.getFieldsValue();

    const invoices = await getInvoices("", cashBox, turn, date, dateTo);

    console.log(invoices);
    if (!invoices.length) {
      setError("No se encontraron datos");
      return;
    }

    const lines = await getInvoiceLines(invoices);
    console.log(lines);
    const paymentLines = await getInvoicePaymentDetails(invoices);
    const reportLines = groupInvoiceLines(lines, paymentLines);

    setData({
      date: form.getFieldValue("date")?.format("MM/DD/YYYY hh:mm A"),
      dateTo: form.getFieldValue("dateTo")?.format("MM/DD/YYYY hh:mm A"),
      company: company,
      invoices: reportLines,
      tpv: cashBox,
      turn: turn,
    });
    setOpen(true);
  };

  const handleSubmit = async () => {
    setError("");
    setLoading(true);

    try {
      const { type } = form.getFieldsValue();
      switch (type) {
        case ReportType.CUADRE_PV:
          await cuadrePVReportData();
          break;

        case ReportType.CUADRE_FP:
          await cuadreFPReportData();
          break;

        case ReportType.CAJERO_FP_DETALLE:
          await invoicesListReportData();
          break;
      }
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  const download = async () => {
    await downloadImage({
      element: componentRef.current,
      fileName: `cuadre-pv-${formattedDate.replace(/\//g, "-")}`,
      onSuccess: (msg: string) => (window as any).messageApi.success(msg),
      onError: (msg: string) => (window as any).messageApi.error(msg),
    });
  };


  async function calculateTotals(
    invoices: any[],
    invoiceLines: any[],
    paymentLines: any[],
    creditMemoLines: any[],
  ): Promise<TotalsResult> {

    let cash = 0, card = 0, disc = 0, ret = 0;
    const paymentForms = await formaDePagoService.getAllFormasDePago();
    const paymentFormMap = new Map(
      paymentForms.map((x) => [x.Code.toLowerCase(), x]),
    );

    for (const invoice of invoices) {
      let payMethodDisc = 0;
      let couponDisc = 0;

      const detalleFactura = paymentLines.filter(
        (x: any) => x.Document_No_ === invoice.Pre_Assigned_No || invoice.No,
      );

      for (const detallePago of detalleFactura) {
        const code = (
          checked ? detallePago?.Forma_de_Pago : detallePago?.Payment_Method
        )?.toLowerCase();

        const fp = paymentFormMap.get(code) as any;
        const amount = checked ? detallePago.Importe : detallePago.Amount;
        payMethodDisc += detallePago.Dto_Amount || detallePago.Descuento || 0;

        // Payment type classification
        const tipo = fp.Tipo_Pago?.toLowerCase();

        if (tipo === "tarjeta") {
          card += amount;
        } else {
          cash += amount;
        }
      }

      // Coupon discounts
      const linesSalesInvoice = invoiceLines.filter(
        (x: any) => x.invoiceNo === invoice.No,
      );

      couponDisc = linesSalesInvoice.reduce(
        (s: number, x: any) => s + (x.Line_Discount_Amount || 0),
        0,
      );

      disc += couponDisc > 0 ? couponDisc : payMethodDisc;
    }

    // Returns
    if (creditMemoLines.length) {
      ret = creditMemoLines.reduce(
        (s: number, x: any) => s + (checked ? x.Line_Amount : x.Unit_Price),
        0,
      );

      if (!checked) {
        ret -= disc;
      }
    }

    return { cash, card, disc, ret };
  }

  useEffect(() => {
    let active = true;

    const loadTurns = async () => {
      const turnsOptions = await getAllTurnos();

      if (!active) return;

      setTurns(
        turnsOptions.map(x => ({
          label: x.DescripcionTurno,
          value: x.No,
        })),
      );
    };

    const loadCashBoxes = async () => {
      const cashBoxesOptions = await getAllCajas();
      setCachBoxes(
        cashBoxesOptions.map((x) => ({
          label: x.Nombre,
          value: x.No,
        })),
      );
    };

    const loadCompany = async () => {
      try {
        const empresa = await getEmpresa();
        setCompany(empresa);
      } catch (error) {
        console.error("Error al cargar datos de la empresa:", error);
      }
    };

    loadTurns();
    loadCashBoxes();
    loadCompany();

    return () => {
      active = false;
    };
  }, []);

  const reportTitle =
    ReportTypeOptions.find((x) => x.value === reportType)?.label ?? "";
  return (
    <div className="caja-management-container">
      <div style={{ display: "flex", alignItems: "center" }}>
        <Title level={2} style={{ margin: 0 }}>
          Informes
        </Title>

        <div style={{ marginLeft: "auto" }}>
          <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span>Consulta desde Business Central</span>
            <Switch checked={checked} onChange={setChecked} />
          </label>
        </div>
      </div>

      <Divider />

      {error && (
        <Alert
          message="Info"
          description={error}
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          closable
          onClose={() => setError("")}
        />
      )}

      <Spin spinning={loading} tip="Cargando...">
        <Form form={form} name="form" layout="vertical" onFinish={handleSubmit}>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="type"
                label="Tipo"
                rules={[{ required: true, message: "Seleccione un tipo" }]}
              >
                <Select
                  placeholder="Selecciona una opción"
                  options={ReportTypeOptions}
                  onChange={(v) => setReportType(v)}
                />
              </Form.Item>
            </Col>
          </Row>

          {reportType && (
            <>
              <Row gutter={16}>
                <Col xs={12} md={6}>
                  <Form.Item
                    name="date"
                    label="Fecha"
                    rules={[{ required: true, message: "Seleccione la fecha" }]}
                  >
                    <DatePicker
                      placeholder="Seleccione fecha"
                      prefix={<CalendarOutlined />}
                    />
                  </Form.Item>
                </Col>

                {reportType == ReportType.CAJERO_FP_DETALLE && (
                  <Col xs={12} md={6}>
                    <Form.Item name="dateTo" label="Fecha fin">
                      <DatePicker
                        placeholder="Seleccione fecha"
                        prefix={<CalendarOutlined />}
                      />
                    </Form.Item>
                  </Col>
                )}
              </Row>

              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="cashbox"
                    label="Caja"
                    rules={[{ required: true, message: "Seleccione la caja" }]}
                  >
                    <Select
                      placeholder="Selecciona una opción"
                      options={cashBoxes}
                      onChange={(v) => setCashBox(v)}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="turn"
                    label="Turno"
                    rules={[{ required: true, message: "Seleccione el turno" }]}
                  >
                    <Select
                      placeholder="Selecciona una opción"
                      options={turns}
                      onChange={(v) => setTurn(v)}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </>
          )}

          <Button loading={loading} type="primary" htmlType="submit">
            Ver
          </Button>
        </Form>
      </Spin>
      <Modal
        title={reportTitle}
        open={open}
        onCancel={() => console.log("cancel")}
        footer={[
          <Button key="download" onClick={download}>
            Descargar Imagen
          </Button>,
          <Button key="print" onClick={() => setOpen(false)}>
            Cancelar
          </Button>,
        ]}
        maskClosable={false}
        closable={false}
        width={400}
        centered
      >
        {reportType == ReportType.CUADRE_PV && (
          <CuadrePV
            cierre={data}
            company={company}
            componentRef={componentRef}
          />
        )}

        {reportType == ReportType.CUADRE_FP && (
          <CuadreFP componentRef={componentRef} data={data} />
        )}

        {reportType == ReportType.CAJERO_FP_DETALLE && (
          <InvoicesList componentRef={componentRef} data={data} />
        )}
      </Modal>
    </div>
  );
};

export default Reports;
